import React, { useEffect } from 'react';
import './DeletionProgressModal.css';

interface FormDeletionProgressModalProps {
  isOpen: boolean;
  isDeleting: boolean;
  totalForms: number;
  deletedCount: number;
  currentFormName?: string;
  onComplete?: () => void;
}

const FormDeletionProgressModal: React.FC<FormDeletionProgressModalProps> = ({
  isOpen,
  isDeleting,
  totalForms,
  deletedCount,
  currentFormName,
  onComplete
}) => {
  const progressPercentage = totalForms > 0 ? (deletedCount / totalForms) * 100 : 0;
  const isComplete = !isDeleting && deletedCount === totalForms;

  // Countdown timer for auto-close
  const [countdown, setCountdown] = React.useState(2);
  
  // Auto-close modal after 2 seconds when deletion is complete
  useEffect(() => {
    if (isComplete && onComplete) {
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            onComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [isComplete, onComplete]);

  if (!isOpen) return null;





  return (
    <div className="deletion-modal-overlay">
      <div className="deletion-modal-container">
        {/* Header */}
        <div className="deletion-modal-header">
          <div className="deletion-modal-icon">
            {isComplete ? (
              <svg className="success-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#10B981" />
                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg className="deleting-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17Z"
                  stroke="#EF4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M9 9L15 15M15 9L9 15" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
          <div className="deletion-modal-title">
            <h3>
              {isComplete 
                ? 'Forms Deleted Successfully' 
                : isDeleting 
                  ? 'Deleting Forms...' 
                  : 'Preparing to Delete'
              }
            </h3>
            <p className="deletion-modal-subtitle">
              {isComplete 
                ? `Successfully deleted ${deletedCount} form${deletedCount !== 1 ? 's' : ''}`
                : `${deletedCount} of ${totalForms} form${totalForms !== 1 ? 's' : ''} deleted`
              }
            </p>
          </div>
        </div>

        {/* Progress Section */}
        <div className="deletion-modal-content">
          {/* Progress Bar */}
          <div className="progress-container">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ 
                  width: `${progressPercentage}%`,
                  backgroundColor: isComplete ? '#10B981' : '#EF4444'
                }}
              />
            </div>
            <div className="progress-text">
              {Math.round(progressPercentage)}%
            </div>
          </div>

          {/* Current Form */}
          {isDeleting && currentFormName && (
            <div className="current-file">
              <div className="current-file-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17Z"
                    stroke="#6B7280"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span className="current-file-name">Deleting: {currentFormName}</span>
            </div>
          )}

          {/* Loading Animation */}
          {isDeleting && (
            <div className="loading-dots">
              <div className="dot" style={{ backgroundColor: '#EF4444' }}></div>
              <div className="dot" style={{ backgroundColor: '#EF4444' }}></div>
              <div className="dot" style={{ backgroundColor: '#EF4444' }}></div>
            </div>
          )}
        </div>

        {/* Footer */}
        {isComplete && (
          <div className="deletion-modal-footer">
            <button
              className="deletion-complete-btn"
              onClick={onComplete}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Done ({countdown}s)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormDeletionProgressModal; 